// ============================================
// CANALAGENTES.JS — Lista de canales + chat por agente
// ============================================
import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { generarReporteMatutino } from './ReporteMatutino';
import './CanalAgentes.css';
import { API_BASE } from '../config/api';
import { OPC_DINERO } from '../utils/dinero';

const PP = "'Poppins', sans-serif";
const STORAGE_PREFIX = 'beep_canal_';
const NOLEIDOS_KEY = 'beep_canales_noleidos';

// FASE 2.2.5: agentes que YA estan modernizados con backend que manda accion
// explicita. Para estos respetamos solo la accion del backend (sin parser por
// keyword). Los demas agentes siguen con codigo viejo y necesitan el parser.
const AGENTES_MODERNOS = ['beep', 'axel'];

const CANALES = [
  { id: 'beep', nombre: 'Beep', rol: 'Director de Orquesta', emoji: '🎯',
    color: '#185FA5', colorFondo: '#E6F1FB', colorOscuro: '#0C447C',
    descripcion: 'Tu asistente principal. Alertas, aprobaciones y coordinación.',
    funciones: ['Alertas del sistema', 'Derivar a especialistas', 'Resumen del día'],
    endpoint: '/api/agente' },
  { id: 'axel', nombre: 'Axel', rol: 'Gerente Comercial', emoji: '💼',
    color: '#0F6E56', colorFondo: '#E1F5EE', colorOscuro: '#085041',
    descripcion: 'Cotizaciones, prospectos y seguimiento comercial.',
    funciones: ['Generar cotización', 'Ver pipeline', 'Dar seguimiento'],
    endpoint: '/api/agente-comercial' },
  { id: 'max', nombre: 'Max', rol: 'Gerente de Operaciones', emoji: '⚙️',
    color: '#854F0B', colorFondo: '#FAEEDA', colorOscuro: '#633806',
    descripcion: 'Servicios en campo, técnicos y programación.',
    funciones: ['Ver servicios hoy', 'Asignar técnico', 'Programar servicio'],
    endpoint: '/api/agente-operaciones' },
  { id: 'rex', nombre: 'Rex', rol: 'Gerente de Proyectos', emoji: '🏗️',
    color: '#0F6E56', colorFondo: '#E1F5EE', colorOscuro: '#085041',
    descripcion: 'Instalaciones nuevas, levantamientos y proyectos.',
    funciones: ['Ver proyectos activos', 'Nuevo levantamiento', 'Avance de obra'],
    endpoint: '/api/agente-proyectos' },
  { id: 'bruno', nombre: 'Bruno', rol: 'Gerente de Almacén', emoji: '📦',
    color: '#534AB7', colorFondo: '#EEEDFE', colorOscuro: '#3C3489',
    descripcion: 'Inventario, kits de material y herramientas.',
    funciones: ['Ver inventario', 'Preparar kit', 'Stock bajo'],
    endpoint: '/api/agente-almacen' },
  { id: 'nova', nombre: 'Nova', rol: 'Gerente Administrativa', emoji: '📋',
    color: '#993556', colorFondo: '#FBEAF0', colorOscuro: '#72243E',
    descripcion: 'Gastos, nómina, RH y administración.',
    funciones: ['Aprobar gastos', 'Ver asistencias', 'Nómina'],
    endpoint: '/api/agente-admin' },
  { id: 'luna', nombre: 'Luna', rol: 'Gerente de Marketing', emoji: '✨',
    color: '#854F0B', colorFondo: '#FAEEDA', colorOscuro: '#633806',
    descripcion: 'Campañas, contenido y métricas de marketing.',
    funciones: ['Nueva campaña', 'Ver métricas', 'Contenido pendiente'],
    endpoint: '/api/agente-marketing' },
  { id: 'aria', nombre: 'Aria', rol: 'Tu asistente personal', emoji: '👩‍💼',
    color: '#185FA5', colorFondo: '#E6F1FB', colorOscuro: '#0C447C',
    descripcion: 'Tu asistente personal ejecutiva. Filtra y prioriza todo para ti.',
    funciones: ['Mi agenda hoy', 'Mis aprobaciones', 'Mis prioridades'],
    endpoint: '/api/agente-aria' },
  { id: 'vera', nombre: 'Vera', rol: 'Asistente del Ing. Javier', emoji: '👩‍🔬',
    color: '#534AB7', colorFondo: '#EEEDFE', colorOscuro: '#3C3489',
    descripcion: 'Asistente personal del Ing. Javier. Coordina su agenda técnica.',
    funciones: ['Agenda técnica', 'Proyectos prioritarios', 'Aprobaciones técnicas'],
    endpoint: '/api/agente-vera' },
];

const BIENVENIDA = {
  beep:  'Hola Ricardo 👋 Soy Beep, tu director de orquesta. Estoy monitoreando todos los departamentos. ¿Qué necesitas hoy?',
  axel:  'Hola 👋 Soy Axel, tu gerente comercial. Puedo ayudarte con cotizaciones, prospectos y seguimiento. ¿Por dónde empezamos?',
  max:   'Hola 👋 Soy Max. Tengo visibilidad de todos los servicios en campo. ¿Qué necesitas saber?',
  rex:   'Hola 👋 Soy Rex. Controlo todos los proyectos e instalaciones. ¿Cómo te puedo ayudar?',
  bruno: 'Hola 👋 Soy Bruno. Tengo el inventario bajo control. ¿Qué necesitas del almacén?',
  nova:  'Hola 👋 Soy Nova. Gestiono todo lo administrativo. ¿En qué te ayudo?',
  luna:  'Hola ✨ Soy Luna. Estoy lista para potenciar el marketing de Beep. ¿Qué campaña armamos?',
  aria:  'Hola Ricardo 👩‍💼 Soy Aria, tu asistente personal. Estoy lista para organizar tu día. ¿Por dónde empezamos?',
  vera:  'Hola Ing. Javier 👩‍🔬 Soy Vera, tu asistente técnica. Tengo tu agenda lista. ¿Qué necesitas?',
};

const ESTADO_AGENTE = {
  beep: 'activo', axel: 'activo', max: 'activo', rex: 'procesando',
  bruno: 'espera', nova: 'activo', luna: 'espera',
};

const COLOR_ESTADO = { activo: '#10B981', procesando: '#F59E0B', espera: '#9CA3AF' };

function leer(key, def) {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : def; } catch { return def; }
}
function guardar(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

function cargarHistorial(canalId) {
  return leer(STORAGE_PREFIX + canalId, []);
}
function guardarHistorial(canalId, msgs) {
  const trimmed = msgs.length > MAX_HISTORIAL ? msgs.slice(-MAX_HISTORIAL) : msgs;
  guardar(STORAGE_PREFIX + canalId, trimmed);
}

function cargarNoLeidos() {
  return leer(NOLEIDOS_KEY, {});
}
function setNoLeidos(canalId, n) {
  const obj = cargarNoLeidos();
  obj[canalId] = n;
  guardar(NOLEIDOS_KEY, obj);
  window.dispatchEvent(new Event('beep:canales-noleidos'));
}
function incNoLeido(canalId) {
  const obj = cargarNoLeidos();
  obj[canalId] = (obj[canalId] || 0) + 1;
  guardar(NOLEIDOS_KEY, obj);
  window.dispatchEvent(new Event('beep:canales-noleidos'));
}

function fechaInteligente(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const hoy = new Date();
  const ayer = new Date(); ayer.setDate(ayer.getDate() - 1);
  if (d.toDateString() === hoy.toDateString()) return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === ayer.toDateString()) return 'Ayer';
  const diff = (hoy - d) / 86400000;
  if (diff < 7) return d.toLocaleDateString('es-MX', { weekday: 'short' });
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

function etiquetaDia(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const hoy = new Date();
  const ayer = new Date(); ayer.setDate(ayer.getDate() - 1);
  if (d.toDateString() === hoy.toDateString()) return 'Hoy';
  if (d.toDateString() === ayer.toDateString()) return 'Ayer';
  return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
}

const MAX_HISTORIAL = 100;

function horaAhora() {
  const d = new Date();
  return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

// FASE 2.2.3: convierte accion explicita del backend a boton renderable.
// Si el agente devuelve {accion:"navegar", ruta:"..."}, mapeamos ruta->label.
// Otras acciones (crear_*, mostrar_*) por ahora no generan boton aqui.
function convertirAccionABoton(accion) {
  if (!accion || typeof accion !== 'object') return [];

  const labelsByRuta = {
    // CO-1.1: oculto sugerencia hacia mock de cotizaciones (PipelineCotizaciones)
    // 'comercial.cotizaciones':         '📝 Ver cotizaciones',
    'comercial.prospectos':           '🎯 Ver prospectos',
    'comercial.levantamientos':       '📏 Ver levantamientos',
    'operaciones.expedientes-todos':  '📋 Ver expedientes',
    'operaciones.servicios':          '🔧 Ver servicios',
    'mitrabajo.midia':                '📊 Ir a mi día',
    'almacen.inventario':             '📦 Ver inventario',
    'administracion.finanzas':        '💰 Ver gastos',
    'mitrabajo.tareas':               '✅ Ver tareas',
    'marketing.campanas':             '📣 Ver marketing',
  };

  if (accion.accion === 'navegar' && accion.ruta) {
    // CO-1.1: filtrar rutas a mocks ocultos para evitar fallback "Ir a ..."
    if (accion.ruta === 'comercial.cotizaciones') return [];
    const label = labelsByRuta[accion.ruta] || `Ir a ${accion.ruta}`;
    return [{ label, ruta: accion.ruta }];
  }

  return [];
}

function parsearMensajeAgente(texto) {
  const acciones = [];
  if (/aprobaci|pendient/i.test(texto))
    acciones.push({ label: '✅ Ir a aprobaciones', ruta: 'mitrabajo.midia' });
  // CO-1.1: oculto sugerencia hacia mock de cotizaciones (PipelineCotizaciones)
  // if (/cotizaci/i.test(texto))
  //   acciones.push({ label: '📝 Ver cotizaciones', ruta: 'comercial.cotizaciones' });
  if (/servicio/i.test(texto))
    acciones.push({ label: '🔧 Ver servicios', ruta: 'operaciones.servicios' });
  if (/proyecto|expediente/i.test(texto))
    acciones.push({ label: '📋 Ver expedientes', ruta: 'operaciones.expedientes-todos' });
  if (/inventario|stock/i.test(texto))
    acciones.push({ label: '📦 Ver inventario', ruta: 'almacen.inventario' });
  if (/gasto/i.test(texto))
    acciones.push({ label: '💰 Ver gastos', ruta: 'administracion.finanzas' });
  if (/tarea/i.test(texto))
    acciones.push({ label: '✅ Ver tareas', ruta: 'mitrabajo.tareas' });
  if (/campa|marketing/i.test(texto))
    acciones.push({ label: '📣 Ver marketing', ruta: 'marketing.campanas' });
  return acciones;
}

function guardarEnBeep(canalId, mensajeUsuario, respuestaAgente) {
  if (canalId === 'beep') return;
  const historialBeep = cargarHistorial('beep');
  const ahora = new Date().toISOString();
  const hora = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  const notaSupervision = {
    autor: 'agente',
    texto: `📋 Supervisión — Canal ${canalId.toUpperCase()}:\n"${mensajeUsuario}"\n→ ${respuestaAgente.substring(0, 100)}...`,
    hora,
    fecha: ahora,
    esSupervision: true,
    canalOrigen: canalId,
  };
  guardarHistorial('beep', [...historialBeep, notaSupervision]);
  incNoLeido('beep');
}

// ============= LISTA DE CANALES =============

function ListaCanales({ canales, noLeidos, onAbrir, movil }) {
  const totalNoLeidos = Object.values(noLeidos).reduce((a, b) => a + (b || 0), 0);
  return (
    <div style={{
      fontFamily: PP, padding: movil ? 12 : 22,
      maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: movil ? 22 : 26, fontWeight: 800, color: '#1a1a1a' }}>Mis agentes</div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>Tu equipo de IA, un canal por especialista</div>
        </div>
        {totalNoLeidos > 0 && (
          <span style={{
            background: '#EF4444', color: '#fff',
            padding: '6px 14px', borderRadius: 999,
            fontSize: 12, fontWeight: 800,
          }}>
            {totalNoLeidos} sin leer
          </span>
        )}
      </div>

      <div style={{
        background: '#fff', borderRadius: 16,
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}>
        {canales.map((c, i) => {
          const hist = cargarHistorial(c.id);
          const ultimo = hist[hist.length - 1];
          const noL = noLeidos[c.id] || 0;
          const ultimoTexto = ultimo ? ultimo.texto : 'Inicia conversación';
          return (
            <div
              key={c.id}
              onClick={() => onAbrir(c.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '14px 16px',
                borderBottom: i < canales.length - 1 ? '1px solid #f0f0f0' : 'none',
                cursor: 'pointer',
                background: noL > 0 ? '#FAFBFC' : '#fff',
              }}
            >
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{
                  width: 52, height: 52, borderRadius: '50%',
                  background: c.colorFondo,
                  border: `2px solid ${c.color}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 24,
                }}>{c.emoji}</div>
                <span style={{
                  position: 'absolute', bottom: 0, right: 0,
                  width: 12, height: 12, borderRadius: '50%',
                  background: COLOR_ESTADO[ESTADO_AGENTE[c.id]] || '#9CA3AF',
                  border: '2px solid #fff',
                }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {c.nombre}
                  </div>
                  {ultimo && (
                    <div style={{ fontSize: 10, color: '#999', flexShrink: 0 }}>{fechaInteligente(ultimo.fecha)}</div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{c.rol}</div>
                <div style={{
                  fontSize: 12, color: noL > 0 ? '#111' : '#666',
                  fontWeight: noL > 0 ? 600 : 400,
                  marginTop: 3,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {ultimoTexto}
                </div>
              </div>
              {noL > 0 && (
                <span style={{
                  background: '#25D366', color: '#fff',
                  borderRadius: 999, minWidth: 22, height: 22,
                  padding: '0 7px',
                  fontSize: 11, fontWeight: 800,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {noL}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============= CHAT DE UN CANAL =============

function ChatCanal({ canal, nombre, onVolver, onNavegar, onCerrar, onMinimizar, modoPanel, movil }) {
  const [mensajes, setMensajes] = useState(() => cargarHistorial(canal.id));
  const [texto, setTexto] = useState('');
  const [escribiendo, setEscribiendo] = useState(false);
  const [gastoConfirm, setGastoConfirm] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // FASE 2.2.5: removidos timerInactRef, timerCierreRef, preguntoCierreRef.
  // La feature de auto-cierre por inactividad creaba un loop (el useEffect
  // se re-disparaba al agregar el msg automatico) que generaba spam cada 3
  // minutos del mensaje "¿Hay algo mas en lo que te pueda ayudar?". Si el
  // usuario quiere cerrar el chat lo hace manualmente.

  useEffect(() => {
    setNoLeidos(canal.id, 0);
    const hist = cargarHistorial(canal.id);
    const hoyStr = new Date().toDateString();
    const ultimo = hist[hist.length - 1];
    const esDeHoy = ultimo && new Date(ultimo.fecha).toDateString() === hoyStr;

    if (hist.length === 0) {
      const bienvenida = {
        autor: 'agente',
        texto: BIENVENIDA[canal.id] || `Hola 👋 Soy ${canal.nombre}.`,
        hora: horaAhora(),
        fecha: new Date().toISOString(),
      };
      const arr = [bienvenida];
      setMensajes(arr);
      guardarHistorial(canal.id, arr);
    }

    // Auto-reporte matutino solo en canal Beep, primera apertura del día
    if (canal.id === 'beep' && !esDeHoy) {
      const arrInicial = cargarHistorial(canal.id);
      setTimeout(() => {
        setEscribiendo(true);
        generarReporteMatutino(nombre)
          .then(({ reporte: r, tipo: t }) => {
            let texto;
            if (t === 'estructurado' && r && typeof r === 'object') {
              const lineas = [r.saludo || ''];
              (r.secciones || []).forEach(sec => {
                lineas.push('');
                lineas.push(sec.titulo);
                (sec.items || []).forEach(it => {
                  lineas.push(`• ${it.texto}${it.badge ? ` (${it.badge})` : ''}`);
                });
              });
              if (r.cierre) { lineas.push(''); lineas.push(r.cierre); }
              texto = lineas.join('\n').trim();
            } else {
              texto = typeof r === 'string' ? r : JSON.stringify(r);
            }
            const msgReporte = {
              autor: 'agente',
              texto,
              hora: horaAhora(),
              fecha: new Date().toISOString(),
            };
            const finales = [...arrInicial, msgReporte];
            setMensajes(finales);
            guardarHistorial(canal.id, finales);
          })
          .catch(() => {})
          .finally(() => setEscribiendo(false));
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canal.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [mensajes, escribiendo]);

  // FASE 2.2.5: el useEffect "Inactivity auto-close" fue removido. Tenia un
  // bug grave: dependia de [mensajes.length, canal.id] y agregaba un mensaje
  // al state, lo que re-disparaba el efecto, reseteaba el guard
  // preguntoCierreRef y volvia a programar el timer. Resultado: spam cada
  // 3 minutos del mensaje "¿Hay algo mas en lo que te pueda ayudar? 😊".
  // Decision: quitar la feature completa. El usuario cierra el chat
  // manualmente con la X.

  const manejarInput = (valor) => {
    setTexto(valor);
  };

  const enviar = async (txt) => {
    const limpio = (txt || '').trim();
    if (!limpio) return;
    const msgUsuario = { autor: 'usuario', texto: limpio, hora: horaAhora(), fecha: new Date().toISOString() };
    const previo = mensajes;
    const nuevos = [...previo, msgUsuario];
    setMensajes(nuevos);
    guardarHistorial(canal.id, nuevos);
    setTexto('');
    setEscribiendo(true);

    try {
      // FASE 1: /api/agente exige authBearer en backend. Otros endpoints
      // de agentes aun no lo exigen pero mandar el token no rompe nada.
      const token = localStorage.getItem('beep_token');
      // FIX-2X: zona horaria del navegador para que el agente conozca la
      // hora local del usuario y no la del servidor (UTC).
      let tz;
      try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { tz = null; }
      const res = await fetch(API_BASE + canal.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          mensaje: limpio,
          historial: nuevos.filter(m => m.autor !== 'sistema').map(m => ({ autor: m.autor, texto: m.texto })),
          nombre: nombre || 'Ricardo',
          rol: 'direccion',
          tz,
        }),
      });
      const data = await res.json();
      const respuesta = data.respuesta || data.mensaje || data.texto || 'No pude procesar tu mensaje.';
      // FASE 2.2.3: guardar accion explicita del backend para respetarla en render
      const msgAgente = {
        autor: 'agente',
        texto: respuesta,
        accion: data.accion || null,
        hora: horaAhora(),
        fecha: new Date().toISOString(),
      };
      const finales = [...nuevos, msgAgente];
      setMensajes(finales);
      guardarHistorial(canal.id, finales);
      guardarEnBeep(canal.id, limpio, respuesta);
      // Aria gasto action
      if (canal.id === 'aria' && data.accion && data.accion.accion === 'registrar_gasto') {
        setGastoConfirm(data.accion.datos);
      }
    } catch (err) {
      const msgError = { autor: 'agente', texto: '⚠️ No pude conectarme. Intenta de nuevo en un momento.', hora: horaAhora(), fecha: new Date().toISOString() };
      const finales = [...nuevos, msgError];
      setMensajes(finales);
      guardarHistorial(canal.id, finales);
    } finally {
      setEscribiendo(false);
    }
  };

  const handleAccion = (ruta) => {
    if (onNavegar) onNavegar(ruta);
    else window.dispatchEvent(new CustomEvent('beep:navegar', { detail: ruta }));
  };

  const sinHistorialReal = mensajes.filter(m => m.autor === 'usuario').length === 0;

  return (
    <div style={{
      fontFamily: PP,
      display: 'flex', flexDirection: 'column',
      height: modoPanel ? '100%' : (movil ? 'calc(100vh - 120px)' : '78vh'),
      background: '#ECE5DD', borderRadius: modoPanel ? 0 : 16, overflow: 'hidden',
      maxWidth: '100%', boxSizing: 'border-box',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 14px',
        background: canal.color,
        flexShrink: 0,
      }}>
        {modoPanel && (
          <button onClick={onVolver} style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', color:'#fff', padding:'4px 8px' }}>←</button>
        )}
        <div style={{
          width: 38, height: 38, borderRadius: '50%',
          background: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20, flexShrink: 0, cursor: !modoPanel ? 'pointer' : 'default',
        }} onClick={!modoPanel ? onVolver : undefined}>{canal.emoji}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>{canal.nombre}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: COLOR_ESTADO[ESTADO_AGENTE[canal.id]] || '#9CA3AF' }} />
            {canal.rol}
          </div>
        </div>
        {modoPanel && (
          <div style={{ display: 'flex', gap: 2 }}>
            {onMinimizar && <button onClick={onMinimizar} style={{ background:'none', border:'none', fontSize:16, cursor:'pointer', color:'rgba(255,255,255,0.7)', padding:'4px 8px' }}>—</button>}
            {onCerrar && <button onClick={onCerrar} style={{ background:'none', border:'none', fontSize:16, cursor:'pointer', color:'rgba(255,255,255,0.8)', padding:'4px 8px' }}>✕</button>}
          </div>
        )}
      </div>

      {/* Mensajes */}
      <div ref={scrollRef} style={{
        flex: 1, overflowY: 'auto', padding: '16px 14px',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        {/* FASE 2.2.6: filtrar mensajes de supervision en el chat de Beep.
            guardarEnBeep agrega un mensaje con esSupervision:true cada vez
            que el usuario habla en otro canal. Beep necesita ese contexto en
            localStorage (para que pueda enterarse de lo que pasa en otras
            areas), pero saturaria el feed visual. Solucion: filtrarlos aqui
            en el render, NO al guardarlos. Solo aplica al canal 'beep' —
            otros canales muestran todo. */}
        {(canal.id === 'beep' ? mensajes.filter(m => !m.esSupervision) : mensajes).map((m, i, mensajesVisibles) => {
          const esUsuario = m.autor === 'usuario';
          // FASE 2.2.5: si el agente es moderno (Beep/Axel), respetar SOLO accion del
          // backend. Si es legacy (Max/Bruno/Nova/etc.), usar parser keyword como fallback.
          // Esto evita botones fantasma cuando el backend ya filtro la accion.
          const esAgenteModerno = AGENTES_MODERNOS.includes(canal.id);
          const acciones = !esUsuario
            ? (esAgenteModerno
                ? convertirAccionABoton(m.accion)
                : parsearMensajeAgente(m.texto))
            : [];
          // FASE 2.2.6: prevMsg ahora referencia el array filtrado (3er arg de
          // map). Esto asegura que el separador de fecha compare con el
          // mensaje VISIBLE anterior, no con uno oculto.
          const prevMsg = i > 0 ? mensajesVisibles[i - 1] : null;
          const mDate = m.fecha ? new Date(m.fecha).toDateString() : '';
          const pDate = prevMsg && prevMsg.fecha ? new Date(prevMsg.fecha).toDateString() : '';
          const showDateSep = mDate && mDate !== pDate;
          return (
            <React.Fragment key={i}>
            {showDateSep && (
              <div style={{ textAlign: 'center', fontSize: 11, color: '#999', margin: '8px 0', fontFamily: PP }}>
                <span style={{ background: '#fff', padding: '3px 12px', borderRadius: 8, fontSize: 11 }}>{etiquetaDia(m.fecha)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: esUsuario ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '80%',
                background: esUsuario ? canal.colorFondo : '#fff',
                color: esUsuario ? canal.colorOscuro : '#111',
                borderRadius: esUsuario ? '12px 0 12px 12px' : '0 12px 12px 12px',
                padding: '10px 14px',
                fontSize: 13, lineHeight: 1.45,
                wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
              }}>
                {/* FASE 2.2.5: renderizar markdown si es agente, texto plano si es usuario.
                    react-markdown con remark-gfm soporta tablas, negritas, listas,
                    blockquotes, codigo, etc. Estilos en CanalAgentes.css. */}
                {esUsuario ? (
                  <div>{m.texto}</div>
                ) : (
                  <div className="markdown-agente">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {m.texto}
                    </ReactMarkdown>
                  </div>
                )}
                {acciones.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {acciones.map((a, j) => (
                      <button
                        key={j}
                        onClick={() => handleAccion(a.ruta)}
                        style={{
                          background: canal.color, color: '#fff',
                          border: 'none', borderRadius: 999, padding: '5px 12px',
                          fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: PP,
                        }}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 9, opacity: 0.6, marginTop: 4, textAlign: 'right' }}>{m.hora}</div>
              </div>
            </div>
            </React.Fragment>
          );
        })}
        {escribiendo && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{
              background: canal.colorFondo,
              borderLeft: `3px solid ${canal.color}`,
              borderRadius: 14, padding: '10px 14px',
              display: 'flex', gap: 4,
            }}>
              {[0, 1, 2].map(i => (
                <span key={i} style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: canal.colorOscuro, opacity: 0.5,
                  animation: 'beepDot 1s ease-in-out infinite',
                  animationDelay: `${i * 0.15}s`,
                }} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tarjeta de confirmación de gasto (Aria) */}
      {gastoConfirm && (
        <div style={{ padding: '10px 14px', borderTop: '1px solid #eee', background: '#FAFBFC' }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 14,
            border: '1px solid #e5e7eb',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#185FA5', marginBottom: 8 }}>💳 Confirmar gasto</div>
            <div style={{ fontSize: 13, color: '#333', marginBottom: 4 }}>{gastoConfirm.descripcion}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a1a', marginBottom: 4 }}>
              ${(gastoConfirm.monto || 0).toLocaleString('es-MX', OPC_DINERO)} {gastoConfirm.moneda || 'MXN'}
            </div>
            <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 10 }}>
              {gastoConfirm.esPersonal ? '👤 Personal' : '🏢 Empresa'}
              {gastoConfirm.fechaLimitePago && ` · Pagar: ${gastoConfirm.fechaLimitePago}`}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => {
                const gastosArr = leer('beep_gastos', []);
                const nuevoGasto = {
                  id: Date.now(), folio: `GST-2026-${String(gastosArr.length + 1).padStart(3, '0')}`,
                  usuario: nombre || 'Ricardo', tipo: 'otro', categoria: gastoConfirm.esPersonal ? 'personal' : 'empresa',
                  descripcion: gastoConfirm.descripcion, monto: gastoConfirm.monto, moneda: gastoConfirm.moneda || 'MXN',
                  montoMXN: gastoConfirm.monto, fecha: new Date().toISOString(),
                  fechaLimitePago: gastoConfirm.fechaLimitePago, notaPago: gastoConfirm.notaPago || '',
                  estado: gastoConfirm.esPersonal ? 'personal' : 'pendiente', esPersonal: !!gastoConfirm.esPersonal,
                  historial: [{ accion: 'Creado via Aria', fecha: new Date().toISOString(), usuario: nombre }],
                };
                guardar('beep_gastos', [...gastosArr, nuevoGasto]);
                window.dispatchEvent(new Event('beep:gastos-actualizados'));
                setGastoConfirm(null);
                const msgOk = { autor: 'agente', texto: '✅ Gasto registrado correctamente.', hora: horaAhora(), fecha: new Date().toISOString() };
                const fin = [...mensajes, msgOk];
                setMensajes(fin);
                guardarHistorial(canal.id, fin);
              }} style={{
                flex: 1, background: '#10B981', color: '#fff', border: 'none', borderRadius: 8,
                padding: '8px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: PP,
              }}>✅ Confirmar y registrar</button>
              <button onClick={() => setGastoConfirm(null)} style={{
                background: 'transparent', color: '#6B7280', border: '1px solid #e5e7eb', borderRadius: 8,
                padding: '8px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: PP,
              }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Chips de funciones rápidas */}
      {/* FIX-2X: ocultos botones rapidos del chat por decision de Ricardo.
          El array `funciones` y la funcion `enviar` se preservan; solo se
          oculta el render. Para reactivar, eliminar el `false &&` de abajo. */}
      {false && sinHistorialReal && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 6,
          padding: '8px 14px',
          borderTop: '1px solid #eee',
          background: '#fff',
        }}>
          {canal.funciones.map((f, i) => (
            <button
              key={i}
              onClick={() => enviar(f)}
              style={{
                background: canal.colorFondo, color: canal.colorOscuro,
                border: `1px solid ${canal.color}55`, borderRadius: 999,
                padding: '6px 12px', fontSize: 11, fontWeight: 600,
                cursor: 'pointer', fontFamily: PP,
              }}
            >
              {f}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{
        display: 'flex', gap: 8, padding: '8px 10px',
        background: '#ECE5DD', alignItems: 'flex-end',
      }}>
        <textarea
          ref={inputRef}
          value={texto}
          onChange={(e) => {
            manejarInput(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px';
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (texto.trim()) { enviar(texto); if (inputRef.current) inputRef.current.style.height = 'auto'; }
            }
          }}
          placeholder={`Mensaje a ${canal.nombre}...`}
          rows={1}
          style={{
            flex: 1, padding: '10px 16px',
            background: '#fff', border: 'none',
            borderRadius: 20, fontSize: 14, fontFamily: PP, outline: 'none',
            resize: 'none', lineHeight: 1.5, overflowY: 'auto',
            maxHeight: 96, wordWrap: 'break-word', whiteSpace: 'pre-wrap',
          }}
        />
        <button
          onClick={() => { enviar(texto); if (inputRef.current) inputRef.current.style.height = 'auto'; }}
          disabled={!texto.trim()}
          style={{
            background: '#25D366', color: '#fff', border: 'none',
            borderRadius: '50%', width: 42, height: 42, flexShrink: 0,
            cursor: texto.trim() ? 'pointer' : 'not-allowed',
            opacity: texto.trim() ? 1 : 0.5, fontSize: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          ➤
        </button>
      </div>

      <style>{`@keyframes beepDot { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }`}</style>
    </div>
  );
}

// ============= COMPONENTE PRINCIPAL =============

export default function CanalAgentes({ nombre, rol, canalInicialId, onNavegar, onCerrar, onMinimizar, modoPanel }) {
  const [canalAbierto, setCanalAbierto] = useState(canalInicialId || null);
  const [noLeidos, setNoLeidosState] = useState(cargarNoLeidos);
  const [ancho, setAncho] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useEffect(() => {
    const h = () => setAncho(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    const sync = () => setNoLeidosState(cargarNoLeidos());
    window.addEventListener('beep:canales-noleidos', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('beep:canales-noleidos', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    if (canalInicialId) setCanalAbierto(canalInicialId);
  }, [canalInicialId]);

  useEffect(() => {
    const handler = () => setCanalAbierto(null);
    window.addEventListener('beep:canal-cerrar', handler);
    return () => window.removeEventListener('beep:canal-cerrar', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const canalId = e.detail;
      if (canalId && CANALES.find(c => c.id === canalId)) {
        setCanalAbierto(canalId);
      }
    };
    window.addEventListener('beep:abrir-canal', handler);
    return () => window.removeEventListener('beep:abrir-canal', handler);
  }, []);

  const movil = ancho < 768;
  const canal = useMemo(() => CANALES.find(c => c.id === canalAbierto), [canalAbierto]);

  if (canal) {
    return (
      <div style={{ fontFamily: PP, padding: modoPanel ? 0 : (movil ? 12 : 22), maxWidth: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <ChatCanal
          canal={canal}
          nombre={nombre}
          onVolver={() => setCanalAbierto(null)}
          onNavegar={onNavegar}
          onCerrar={onCerrar}
          onMinimizar={onMinimizar}
          modoPanel={modoPanel}
          movil={movil || modoPanel}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {modoPanel && (
        <div style={{
          padding: '14px 16px', borderBottom: '1px solid #eee',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', flexShrink: 0,
        }}>
          <div style={{ fontWeight: 700, fontSize: 16, fontFamily: PP, color: '#1a1a1a' }}>BeepChat</div>
          <div style={{ display: 'flex', gap: 4 }}>
            {onMinimizar && <button onClick={onMinimizar} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#888', padding: '4px 8px' }}>—</button>}
            <button onClick={onCerrar} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888', padding: '4px 8px' }}>✕</button>
          </div>
        </div>
      )}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <ListaCanales
          canales={CANALES}
          noLeidos={noLeidos}
          onAbrir={setCanalAbierto}
          movil={movil || modoPanel}
        />
      </div>
    </div>
  );
}
